import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { toProtectedFileUrl } from '../../utils/secureFile';
import {
  Box, Typography, Button, Stack, CircularProgress, Alert, Snackbar,
  Fade, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, IconButton, MenuItem, FormControl, Select, Checkbox,
  Card, CardContent, Divider, Tooltip, Paper
} from '@mui/material';
import {
  Refresh, Add, Visibility, NavigateBefore, NavigateNext, ShoppingCart,
  Receipt, ExpandMore, ExpandLess, Person, LocalShipping, CheckCircle,
  PhotoCamera, Send, RequestQuote, CloudUpload, DoneAll
} from '@mui/icons-material';
import { getEffectivePurchaseStatus } from './purchaseStatusUtils';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getSupplierBarang, addDetailTransaksi,
  updateItemsDikirim, updatePengiriman, mintaPenagihan, updatePembayaran,
  getPembelianByPenerima
} from '../API/supplierPenyedia';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';
const fotoUrl = (name) => (name ? toProtectedFileUrl(`images/${name}`) : null);
const formatRp = (n) => (!n && n !== 0) ? '-' : 'Rp ' + Number(n).toLocaleString('id-ID');
const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const getTotalTransaksi = (row) =>
  row.totalTransaksiKeseluruhan ?? (row.Pembelian || []).reduce((s, p) => s + (Number(p.totalTransaksi) || 0), 0);
const getJumlahItem = (row) =>
  row.jumlahItem ?? (row.Pembelian || []).reduce((s, p) => s + (p.DetailTransaksi?.length || 0), 0);

const PENGIRIMAN_COLOR = { 'Menunggu Respon': 'default', 'Diproses': 'info', 'Dikirim': 'warning', 'Sampai': 'success' };
const BAYAR_COLOR = { 'Belum Dibayar': 'error', 'Sudah Dibayar': 'success' };
const TAGIH_COLOR = { 'Belum Ditagih': 'default', 'Ditagihkan': 'warning', 'Lunas': 'success' };

// ---- Panel kelola pengiriman (supplier) ----
const PengirimanPanel = ({ pembelian, isSupplier, canManageBayar, onChanged, notify }) => {
  const [checked, setChecked] = useState(() => new Set((pembelian.DetailTransaksi || []).filter(d => d.dikirim).map(d => d.id)));
  const [savingItems, setSavingItems] = useState(false);
  const [status, setStatus] = useState(getEffectivePurchaseStatus(pembelian));
  const [catatan, setCatatan] = useState(pembelian.catatanPengiriman || '');
  const [fotoKirim, setFotoKirim] = useState(null);
  const [fotoSampai, setFotoSampai] = useState(null);
  const [savingKirim, setSavingKirim] = useState(false);

  useEffect(() => {
    setChecked(new Set((pembelian.DetailTransaksi || []).filter(d => d.dikirim).map(d => d.id)));
    setStatus(getEffectivePurchaseStatus(pembelian));
    setCatatan(pembelian.catatanPengiriman || '');
  }, [pembelian]);

  const toggle = (id) => setChecked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const saveItems = async () => {
    setSavingItems(true);
    try {
      await updateItemsDikirim(pembelian.id, [...checked]);
      notify('Barang kirim disimpan');
      onChanged();
    } catch (e) { notify(e.response?.data?.message || 'Gagal simpan', 'error'); }
    finally { setSavingItems(false); }
  };

  const saveKirim = async () => {
    setSavingKirim(true);
    try {
      const fd = new FormData();
      fd.append('statusPengiriman', status);
      fd.append('catatanPengiriman', catatan);
      if (fotoKirim) fd.append('fotoPengiriman', fotoKirim);
      if (fotoSampai) fd.append('fotoSampai', fotoSampai);
      await updatePengiriman(pembelian.id, fd);
      notify('Pengiriman diperbarui');
      setFotoKirim(null); setFotoSampai(null);
      onChanged();
    } catch (e) { notify(e.response?.data?.message || 'Gagal simpan', 'error'); }
    finally { setSavingKirim(false); }
  };

  const handleBayar = async (val) => {
    try { await updatePembayaran(pembelian.id, val); notify('Status pembayaran diperbarui'); onChanged(); }
    catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
  };

  const handleTagih = async () => {
    try { await mintaPenagihan(pembelian.id); notify('Penagihan diajukan'); onChanged(); }
    catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
  };

  const items = pembelian.DetailTransaksi || [];

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#fafbfc' }}>
      {/* Status ringkas */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1.5, gap: 0.5 }}>
        <Chip size="small" icon={<LocalShipping sx={{ fontSize: 14 }} />} label={getEffectivePurchaseStatus(pembelian)} color={PENGIRIMAN_COLOR[getEffectivePurchaseStatus(pembelian)] || 'default'} />
        <Chip size="small" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={pembelian.statusPembayaran} color={BAYAR_COLOR[pembelian.statusPembayaran] || 'default'} variant={pembelian.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
        <Chip size="small" label={`Penagihan: ${pembelian.statusPenagihan}`} color={TAGIH_COLOR[pembelian.statusPenagihan] || 'default'} variant="outlined" />
      </Stack>

      {/* Checklist barang (supplier) */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Barang yang dikirim ({items.filter(d => d.dikirim).length}/{items.length})
      </Typography>
      <Stack spacing={0.25} sx={{ mb: 1 }}>
        {items.map((d) => (
          <Stack key={d.id} direction="row" alignItems="center" spacing={0.5}
            sx={{ py: 0.25, px: 0.5, borderRadius: 1, bgcolor: d.dikirim ? '#e8f5e9' : 'transparent' }}>
            {isSupplier ? (
              <Checkbox size="small" checked={checked.has(d.id)} onChange={() => toggle(d.id)} sx={{ p: 0.25 }} />
            ) : (
              <Checkbox size="small" checked={!!d.dikirim} disabled sx={{ p: 0.25 }} />
            )}
            <Typography variant="body2" sx={{ flex: 1 }}>{d.namaBarang}</Typography>
            <Typography variant="caption" color="text.secondary">{d.jumlahBarang} {d.satuan}</Typography>
            <Typography variant="caption" fontWeight={600}>{formatRp(d.totalTransaksi)}</Typography>
            {d.dikirim && <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />}
          </Stack>
        ))}
      </Stack>
      {isSupplier && (
        <Button size="small" variant="outlined" disabled={savingItems} onClick={saveItems} sx={{ mb: 1.5, textTransform: 'none' }}>
          {savingItems ? 'Menyimpan...' : 'Simpan Barang Kirim'}
        </Button>
      )}

      {/* Update pengiriman (supplier) */}
      {isSupplier && (
        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>Update Pengiriman</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="Diproses">Diproses</MenuItem>
                <MenuItem value="Dikirim">Dikirim</MenuItem>
                <MenuItem value="Sampai">Sampai</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Catatan pengiriman" value={catatan} onChange={(e) => setCatatan(e.target.value)} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
            <Button component="label" size="small" variant="outlined" startIcon={<PhotoCamera />} sx={{ textTransform: 'none' }}>
              {fotoKirim ? fotoKirim.name : 'Foto Pengiriman'}
              <input hidden type="file" accept="image/*" onChange={(e) => setFotoKirim(e.target.files?.[0] || null)} />
            </Button>
            <Button component="label" size="small" variant="outlined" startIcon={<PhotoCamera />} sx={{ textTransform: 'none' }}>
              {fotoSampai ? fotoSampai.name : 'Foto Barang Sampai'}
              <input hidden type="file" accept="image/*" onChange={(e) => setFotoSampai(e.target.files?.[0] || null)} />
            </Button>
          </Stack>
          <Button size="small" variant="contained" disabled={savingKirim} onClick={saveKirim} startIcon={<Send />} sx={{ textTransform: 'none' }}>
            {savingKirim ? 'Menyimpan...' : 'Simpan Pengiriman'}
          </Button>
        </Box>
      )}

      {/* Info foto & tanggal pengiriman */}
      {(pembelian.fotoPengiriman || pembelian.fotoSampai || pembelian.tanggalKirim || pembelian.tanggalSampai) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5 }}>
          {pembelian.tanggalKirim && <Chip size="small" variant="outlined" label={`Kirim: ${formatDate(pembelian.tanggalKirim)}`} />}
          {pembelian.tanggalSampai && <Chip size="small" variant="outlined" color="success" label={`Sampai: ${formatDate(pembelian.tanggalSampai)}`} />}
          {pembelian.fotoPengiriman && <Button size="small" onClick={() => openInAppFilePreview({ url: fotoUrl(pembelian.fotoPengiriman), title: 'Foto Pengiriman' })} sx={{ textTransform: 'none' }}>Foto Kirim</Button>}
          {pembelian.fotoSampai && <Button size="small" onClick={() => openInAppFilePreview({ url: fotoUrl(pembelian.fotoSampai), title: 'Foto Sampai' })} sx={{ textTransform: 'none' }}>Foto Sampai</Button>}
        </Stack>
      )}
      {pembelian.catatanPengiriman && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Catatan: {pembelian.catatanPengiriman}
        </Typography>
      )}

      {/* Pembayaran & penagihan */}
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
        {canManageBayar && (
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select value={pembelian.statusPembayaran} onChange={(e) => handleBayar(e.target.value)}>
              <MenuItem value="Belum Dibayar">Belum Dibayar</MenuItem>
              <MenuItem value="Sudah Dibayar">Sudah Dibayar</MenuItem>
            </Select>
          </FormControl>
        )}
        {isSupplier && pembelian.statusPembayaran !== 'Sudah Dibayar' && pembelian.statusPenagihan !== 'Ditagihkan' && (
          <Button size="small" variant="outlined" color="warning" startIcon={<RequestQuote />} onClick={handleTagih} sx={{ textTransform: 'none' }}>
            Minta Penagihan
          </Button>
        )}
        {pembelian.statusPenagihan === 'Ditagihkan' && (
          <Chip size="small" color="warning" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={`Ditagihkan ${formatDate(pembelian.tanggalPenagihan)}`} />
        )}
        {pembelian.statusPembayaran === 'Sudah Dibayar' && (
          <Chip size="small" color="success" icon={<DoneAll sx={{ fontSize: 14 }} />} label="Lunas" />
        )}
      </Stack>
    </Box>
  );
};

const DataPembelianSupplier = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.authAdmin?.user);
  const userRole = user?.role || localStorage.getItem('role') || '';
  const roleNorm = String(userRole).trim().toLowerCase().replace(/[\s_-]+/g, '');

  const isAdmin = roleNorm === 'admin';
  const isTFL = roleNorm === 'tfl';
  // Cocokkan variasi role supplier: "Supplier Penyedia", "supplierpenyedia", dll.
  const isSupplier = roleNorm === 'supplierpenyedia' || roleNorm.includes('supplier');
  const canAdd = isAdmin || isTFL;
  const canManageBayar = ['admin', 'tfl', 'ppk', 'kabid', 'kadis'].includes(roleNorm);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedId, setExpandedId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ SupplierPembelianBarangId: '', namaBarang: '', hargaSatuan: '', jumlahBarang: '', kategoriPembelian: 'Pembelian Tahap 1' });
  const [saving, setSaving] = useState(false);

  const isMobile = useMediaQuery('(max-width:800px)');
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      if (isSupplier) {
        // Supplier: kelompokkan pembelian per penerima bantuan
        const res = await getPembelianByPenerima({ page, limit, search });
        setRows(res.data.data || []);
        setTotalItems(res.data.totalItems ?? 0);
        setTotalPages(res.data.totalPages ?? 1);
      } else {
        const res = await getSupplierBarang({ page, limit, search });
        setRows(res.data.data || []);
        setTotalItems(res.data.totalItems ?? 0);
        setTotalPages(res.data.totalPages ?? 1);
      }
    } catch (err) {
      console.error(err);
      notify('Gagal mengambil data pembelian', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, isSupplier]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(), 400);
    return () => clearTimeout(t);
  }, [fetchList]);

  const toggleExpand = (id) => setExpandedId((p) => (p === id ? null : id));

  const openAddItem = (pembelianId) => {
    setAddForm({ SupplierPembelianBarangId: pembelianId, namaBarang: '', hargaSatuan: '', jumlahBarang: '', kategoriPembelian: 'Pembelian Tahap 1' });
    setAddOpen(true);
  };

  const handleAddItem = async () => {
    if (!addForm.namaBarang.trim()) return notify('Nama barang wajib diisi', 'warning');
    if (!addForm.hargaSatuan || Number(addForm.hargaSatuan) <= 0) return notify('Harga satuan tidak valid', 'warning');
    if (!addForm.jumlahBarang || Number(addForm.jumlahBarang) <= 0) return notify('Jumlah tidak valid', 'warning');
    setSaving(true);
    try {
      await addDetailTransaksi({
        SupplierPembelianBarangId: addForm.SupplierPembelianBarangId,
        namaBarang: addForm.namaBarang.trim(),
        hargaSatuan: Number(addForm.hargaSatuan),
        jumlahBarang: Number(addForm.jumlahBarang),
        kategoriPembelian: addForm.kategoriPembelian || 'Pembelian Tahap 1',
      });
      notify('Item berhasil ditambahkan');
      setAddOpen(false);
      fetchList();
    } catch (err) {
      notify(err.response?.data?.message || 'Gagal menambah item', 'error');
    } finally {
      setSaving(false);
    }
  };

  const contentStyle = isMobile ? {} : { marginLeft: "250px", marginRight: "20px" };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ mt: 1, mb: 2, width: '100%', maxWidth: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" />
              <Typography variant="h5" fontWeight={700}>
                {isSupplier ? 'Pembelian per Penerima Bantuan' : 'Data Pembelian Supplier'}
              </Typography>
              <Chip size="small" label={userRole || 'Guest'} color={isAdmin ? 'error' : isTFL ? 'warning' : isSupplier ? 'info' : 'default'} icon={<Person />} variant="outlined" />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {isSupplier && (
                <Button variant="outlined" size="small" href="/supplier/bukti-transfer" sx={{ textTransform: 'none' }}>
                  Monitoring Bukti Transfer
                </Button>
              )}
              <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchList}>Muat Ulang</Button>
            </Box>
          </Box>

          {isSupplier && (
            <Alert severity="info" sx={{ mb: 2 }} icon={<LocalShipping />}>
              Data dikelompokkan per penerima bantuan. Setiap baris menampilkan total transaksi dan rincian barang yang dibeli penerima dari toko Anda. Klik ikon detail untuk melihat barang & memproses pengiriman.
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <TextField size="small" placeholder={isSupplier ? 'Cari nama penerima bantuan...' : 'Cari nama supplier...'} value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ minWidth: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          </Box>

          {isSupplier ? (
            <Box sx={{ overflowX: 'auto', bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 2,
              '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 } }}>
              <table>
                <thead style={{ backgroundColor: '#f5f7fa' }}>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>No.</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Penerima Bantuan</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Total Transaksi</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Jumlah Item</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Belum ada pembelian dari penerima bantuan.</td></tr>
                  ) : rows.map((row, i) => {
                    const key = row.bantuanId ?? `x-${i}`;
                    const expanded = expandedId === key;
                    return (
                      <React.Fragment key={key}>
                        <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: expanded ? '#f9fafc' : 'inherit' }}>
                          <td style={{ padding: '10px 16px' }}>{(page - 1) * limit + i + 1}</td>
                          <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                              {row.namaPenerima}
                            </Box>
                            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, ml: 2.5 }}>
                              {[row.desaKelurahan, row.kecamatan].filter((v) => v && v !== '-').join(', ')}
                              {row.jenisBantuan && row.jenisBantuan !== '-' ? ` · ${row.jenisBantuan}` : ''}
                            </Box>
                          </td>
                          <td style={{ padding: '10px 16px', fontWeight: 700, color: '#1976d2' }}>
                            {formatRp(row.totalTransaksi)}
                            {row.jumlahTransaksi > 1 && (
                              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                                {row.jumlahTransaksi} transaksi
                              </Box>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px' }}>{row.jumlahItem} item</td>
                          <td>
                            <IconButton size="small" color="primary" onClick={() => toggleExpand(key)}>
                              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={5} style={{ padding: '12px 24px', backgroundColor: '#fafbfc' }}>
                              {(row.namaTFL !== '-' || row.namaAgr !== '-') && (
                                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
                                  {row.namaTFL !== '-' && <Chip size="small" variant="outlined" color="primary" label={`TFL: ${row.namaTFL}`} sx={{ fontSize: 11, height: 22 }} />}
                                  {row.namaAgr !== '-' && <Chip size="small" variant="outlined" color="secondary" label={`AGR: ${row.namaAgr}`} sx={{ fontSize: 11, height: 22 }} />}
                                </Stack>
                              )}

                              {/* Rincian barang yang dibeli penerima */}
                              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Barang yang dibeli ({row.items.length})
                              </Typography>
                              <Box sx={{ overflowX: 'auto', mb: 1.5, border: '1px solid #eef1f5', borderRadius: 1 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                  <thead style={{ backgroundColor: '#f0f2f5' }}>
                                    <tr>
                                      <th style={{ textAlign: 'left', padding: '6px 10px' }}>Nama Barang</th>
                                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>Harga</th>
                                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>Jumlah</th>
                                      <th style={{ textAlign: 'right', padding: '6px 10px' }}>Subtotal</th>
                                      <th style={{ textAlign: 'center', padding: '6px 10px' }}>Dikirim</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {row.items.map((it) => (
                                      <tr key={it.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '6px 10px' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                            <span>{it.namaBarang}</span>
                                            {it.kategoriPembelian && <Chip size="small" label={it.kategoriPembelian} variant="outlined" color="primary" sx={{ fontSize: 10, height: 20 }} />}
                                          </Box>
                                        </td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatRp(it.hargaSatuan)}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{it.jumlahBarang} {it.satuan || ''}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{formatRp(it.totalTransaksi)}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                          {it.dikirim ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} /> : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>

                              {/* Daftar transaksi + tombol proses pengiriman */}
                              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Transaksi ({row.pembelian.length})
                              </Typography>
                              <Stack spacing={1}>
                                {row.pembelian.map((p) => (
                                  <Stack key={p.id} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1}
                                    sx={{ p: 1, border: '1px solid #eef1f5', borderRadius: 1, bgcolor: '#fff' }}>
                                    <Typography variant="body2" sx={{ minWidth: 150 }}>
                                      <Receipt fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                      Transaksi #{p.id} · {formatDate(p.createdAt)}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary" sx={{ minWidth: 120 }}>
                                      {formatRp(p.totalTransaksi)}
                                    </Typography>
                                    <Chip size="small" icon={<LocalShipping sx={{ fontSize: 14 }} />} label={getEffectivePurchaseStatus(p)} color={PENGIRIMAN_COLOR[getEffectivePurchaseStatus(p)] || 'default'} />
                                    <Chip size="small" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={p.statusPembayaran} color={BAYAR_COLOR[p.statusPembayaran] || 'default'} variant={p.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
                                    <Button size="small" variant="contained" color="primary" startIcon={<LocalShipping />}
                                      onClick={() => navigate(`/supplier/pengiriman/${p.id}`)}
                                      sx={{ textTransform: 'none', ml: { sm: 'auto !important' } }}>
                                      Proses Pengiriman
                                    </Button>
                                  </Stack>
                                ))}
                              </Stack>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          ) : (
          <Box sx={{ overflowX: 'auto', bgcolor: '#fff', border: '1px solid #e0e0e0', borderRadius: 2,
            '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 } }}>
            <table>
              <thead style={{ backgroundColor: '#f5f7fa' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>No.</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Supplier</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Total Transaksi</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Jumlah Item</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #e0e0e0', fontWeight: 600 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ padding: '32px', textAlign: 'center' }}><CircularProgress size={26} /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Tidak ada data.</td></tr>
                ) : rows.map((row, i) => {
                  const pembelianList = row.Pembelian || [];
                  const totalTransaksi = getTotalTransaksi(row);
                  const itemCount = getJumlahItem(row);
                  const expanded = expandedId === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: expanded ? '#f9fafc' : 'inherit' }}>
                        <td style={{ padding: '10px 16px' }}>{(page - 1) * limit + i + 1}</td>
                        <td style={{ padding: '10px 16px', fontWeight: 600 }}>{row.namaSupplier}</td>
                        <td style={{ padding: '10px 16px', fontWeight: 700, color: '#1976d2' }}>
                          {formatRp(totalTransaksi)}
                          {pembelianList.length > 1 && (
                            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                              {pembelianList.length} transaksi
                            </Box>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px' }}>{itemCount} item</td>
                        <td>
                          <IconButton size="small" color="primary" onClick={() => toggleExpand(row.id)}>
                            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                          {canAdd && pembelianList[0] && (
                            <Button size="small" variant="outlined" color="success" startIcon={<Add />}
                              onClick={() => openAddItem(pembelianList[0].id)}
                              sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem', py: 0.3, px: 1 }}>
                              Item
                            </Button>
                          )}
                        </td>
                      </tr>
                      {expanded && pembelianList.length > 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: '12px 24px', backgroundColor: '#fafbfc' }}>
                            {pembelianList.map((pembelian, idx) => {
                              const b = pembelian.Bantuan || {};
                              const details = pembelian.DetailTransaksi || [];
                              const dikirimCount = details.filter(d => d.dikirim).length;
                              return (
                              <Card key={pembelian.id} variant="outlined" sx={{ mb: idx < pembelianList.length - 1 ? 2 : 0 }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5} alignItems={{ sm: 'flex-start' }}>
                                    <Box>
                                      <Typography variant="subtitle2">
                                        <Receipt fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                        Transaksi #{pembelian.id} · {formatDate(pembelian.createdAt)}
                                      </Typography>
                                      {b.namaLengkap && (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          <Person sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: 'text.secondary' }} />
                                          <strong>{b.namaLengkap}</strong>
                                          {b.JenisBantuan ? <span style={{ color: '#6b7280' }}> — {b.JenisBantuan}</span> : ''}
                                        </Typography>
                                      )}
                                      {(b.kecamatan || b.desaKelurahan) && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.5 }}>
                                          {[b.desaKelurahan, b.kecamatan].filter(Boolean).join(', ')}
                                        </Typography>
                                      )}
                                      {(b.namaTFL || b.namaAgr) && (
                                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, ml: 2.5 }} flexWrap="wrap">
                                          {b.namaTFL && <Chip size="small" variant="outlined" color="primary" label={`TFL: ${b.namaTFL}`} sx={{ fontSize: 11, height: 22 }} />}
                                          {b.namaAgr && <Chip size="small" variant="outlined" color="secondary" label={`AGR: ${b.namaAgr}`} sx={{ fontSize: 11, height: 22 }} />}
                                        </Stack>
                                      )}
                                    </Box>
                                    <Box sx={{ textAlign: { sm: 'right' } }}>
                                      <Typography variant="subtitle2" fontWeight={700} color="primary">
                                        {formatRp(pembelian.totalTransaksi)}
                                      </Typography>
                                      <Chip size="small" variant="outlined"
                                        color={dikirimCount === details.length && details.length > 0 ? 'success' : 'default'}
                                        label={`${dikirimCount}/${details.length} barang dikirim`}
                                        sx={{ fontSize: 11, height: 22, mt: 0.5 }} />
                                    </Box>
                                  </Stack>

                                  {/* Status ringkas + tombol ke halaman pengiriman */}
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5, alignItems: 'center' }}>
                                    {details.some((d) => d.kategoriPembelian) && (
                                      <Chip size="small" color="primary" variant="outlined" label={details.map((d) => d.kategoriPembelian).filter(Boolean).find(Boolean) || 'Kategori'} />
                                    )}
                                    <Chip size="small" icon={<LocalShipping sx={{ fontSize: 14 }} />} label={getEffectivePurchaseStatus(pembelian)} color={PENGIRIMAN_COLOR[getEffectivePurchaseStatus(pembelian)] || 'default'} />
                                    <Chip size="small" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={pembelian.statusPembayaran} color={BAYAR_COLOR[pembelian.statusPembayaran] || 'default'} variant={pembelian.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
                                    {pembelian.statusPembayaran === 'Sudah Dibayar' && (
                                      <Chip size="small" color="success" icon={<DoneAll sx={{ fontSize: 14 }} />} label="Lunas" />
                                    )}
                                    <Button size="small" variant="contained" color="primary" startIcon={<LocalShipping />}
                                      onClick={() => navigate(`/supplier/pengiriman/${pembelian.id}`)}
                                      sx={{ textTransform: 'none', ml: 'auto !important' }}>
                                      {isSupplier ? 'Proses Pengiriman' : 'Lihat Detail Pengiriman'}
                                    </Button>
                                  </Stack>
                                </CardContent>
                              </Card>
                              );
                            })}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                </tbody>
            </table>
          </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 80, maxWidth: 100 }}>
                <Select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} displayEmpty
                  sx={{ borderRadius: 2, backgroundColor: '#fff', '& .MuiSelect-select': { padding: '6px 14px', fontSize: '0.875rem' } }}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">Hal. {page} / {totalPages} ({totalItems} data)</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<NavigateBefore />} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} sx={{ borderRadius: 2 }}>
                {!isMobile && 'Prev'}
              </Button>
              <Button variant="outlined" size="small" endIcon={<NavigateNext />} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} sx={{ borderRadius: 2 }}>
                {!isMobile && 'Next'}
              </Button>
            </Stack>
          </Box>

          {/* Modal tambah item */}
          <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'success.main', color: 'white', py: 2 }}>
              <Add /> Tambah Item Transaksi
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <TextField label="Nama Barang" size="small" required value={addForm.namaBarang}
                  onChange={(e) => setAddForm((p) => ({ ...p, namaBarang: e.target.value }))} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField label="Jumlah" size="small" type="number" required value={addForm.jumlahBarang}
                  onChange={(e) => setAddForm((p) => ({ ...p, jumlahBarang: e.target.value }))} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField label="Harga Satuan" size="small" type="number" required value={addForm.hargaSatuan}
                  onChange={(e) => setAddForm((p) => ({ ...p, hargaSatuan: e.target.value }))} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                <TextField select label="Kategori" size="small" value={addForm.kategoriPembelian || 'Pembelian Tahap 1'} onChange={(e) => setAddForm((p) => ({ ...p, kategoriPembelian: e.target.value }))} fullWidth>
                  <MenuItem value="Pembelian Tahap 1">Pembelian Tahap 1</MenuItem>
                  <MenuItem value="Pembelian Tahap 2">Pembelian Tahap 2</MenuItem>
                </TextField>
                <Box sx={{ p: 2, bgcolor: '#f5f7fa', borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatRp((Number(addForm.hargaSatuan) || 0) * (Number(addForm.jumlahBarang) || 0))}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setAddOpen(false)} sx={{ borderRadius: 2 }}>Batal</Button>
              <Button variant="contained" color="success" disabled={saving} onClick={handleAddItem} sx={{ borderRadius: 2 }}>
                {saving ? 'Menyimpan...' : 'Tambah'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            TransitionComponent={Fade} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </div>
    </div>
  );
};

export default DataPembelianSupplier;
